import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordField, DRIVER_PW_MIN } from "./PasswordField";
import { errMsg } from "@/pages/Drivers";

// Password: view the current one, or set a new one (signs the driver out everywhere).
export const LoginCard = ({ driver, api, onReset }) => {
  const [shown, setShown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (shown !== null) { setShown(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/drivers/${driver.user_id}/password`);
      setShown(data.password || "");
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/drivers/${driver.user_id}/password`, { password: pw });
      setResetting(false);
      setShown(null);
      onReset(pw);
      setPw("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="driver-login-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div><span className="text-xs uppercase tracking-wider text-slate-500 mr-2">Username</span><span className="font-mono font-semibold" data-testid="driver-login-username-value">{driver.username || "Not set"}</span></div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Password</span>
          <span className="font-mono" data-testid="driver-password-value">{shown === null ? (driver.has_login ? "••••••••" : "Not set") : shown || "Not available — set a new one"}</span>
          {driver.has_login && (
            <Button size="sm" variant="ghost" onClick={toggle} disabled={loading} className="h-8 px-2" data-testid="driver-password-toggle">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : shown === null ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="ml-1">{shown === null ? "Show" : "Hide"}</span>
            </Button>
          )}
        </div>
        {!resetting && (
          <Button size="sm" variant="outline" onClick={() => setResetting(true)} disabled={!driver.username} className="ml-auto" data-testid="driver-reset-password-btn">
            <KeyRound className="w-4 h-4 mr-1.5" /> {driver.has_login ? "Reset password" : "Set password"}
          </Button>
        )}
      </div>
      {!driver.username && <p className="text-xs text-amber-700">Save a username below first, then set a password.</p>}
      {resetting && (
        <div className="space-y-3">
          <PasswordField value={pw} onChange={setPw} label="New password" min={DRIVER_PW_MIN} />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setResetting(false); setPw(""); }} data-testid="driver-reset-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={save} disabled={pw.length < DRIVER_PW_MIN || busy} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="driver-reset-save-btn">{busy ? "Saving…" : "Save new password"}</Button>
          </div>
          <p className="text-xs text-slate-500">The driver is signed out of other phones and must use the new password.</p>
        </div>
      )}
    </div>
  );
};
